import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase/firebase';
import { collection, query, where, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { Link, useNavigate } from 'react-router-dom';

const ProfilePage = () => {
  const [user, setUser] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      if (!user) {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const loadFavorites = async () => {
      if (!user) return;

      try {
        const q = query(collection(db, "favorites"), where("userId", "==", user.uid));
        const querySnapshot = await getDocs(q);
        const favoritePlaces = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setFavorites(favoritePlaces);
      } catch (err) {
        setError("Failed to load favorites: " + err.message);
      } finally {
        setLoading(false);
      }
    };

    loadFavorites();
  }, [user]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/auth');
    } catch (err) {
      setError("Failed to sign out: " + err.message);
    }
  };

  const toggleFavorite = async (place) => {
    if (!user) return;
    
    try {
      const docRef = doc(db, "favorites", `${user.uid}_${place.placeId}`);
      await deleteDoc(docRef);
      setFavorites(prev => prev.filter(fav => fav.placeId !== place.placeId));
    } catch (err) {
      setError("Failed to remove favorite: " + err.message);
    }
  };

  const viewOnMaps = (place) => {
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${place.name} ${place.address}`;
    window.open(mapsUrl, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Please sign in to view your profile</h2>
          </div>
        </div>
      </div>
    );
  }

  return (
<div className="min-h-screen bg-gray-50 py-8">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-2xl font-bold text-left text-gray-900">Your Profile</h2>
          <p className="text-gray-600">Details and favorite places</p>
        </div>
        <div className="flex space-x-4">
          <Link 
            to="/places" 
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Find Places
          </Link>
          <button
            onClick={handleLogout}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            Log Out
          </button>
        </div>
      </div>
      
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-2 text-left">Profile Information</h3>
        <div className="text-left">
          <p className="text-gray-600">Email: {user.email}</p>
        </div>
      </div>

        <div className="mt-8">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4 text-left">Favorite Places</h3>
          
          {error && (
            <div className="bg-red-50 p-4 rounded-md mb-4">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Error</h3>
                  <div className="mt-2 text-sm text-red-700">{error}</div>
                </div>
              </div>
            </div>
          )}

          {favorites.length === 0 ? (
            <div className="bg-white shadow overflow-hidden sm:rounded-lg p-6 text-center">
              <p className="text-gray-500">You haven't favorited any places yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {favorites.map((favorite) => (
                <div key={favorite.id} className="bg-white overflow-hidden shadow rounded-lg">
                  <div className="p-6">
                    <h4 className="text-lg font-medium text-gray-900">{favorite.name}</h4>
                    <p className="mt-1 text-sm text-gray-500">{favorite.address}</p>
                    
                    {/* Action Buttons */}
                    <div className="mt-4 flex space-x-2">
                      <button
                        onClick={() => toggleFavorite(favorite)}
                        className="text-sm font-medium rounded px-3 py-1 bg-yellow-300 text-yellow-900"
                      >
                        ★ Remove Favorite
                      </button>
                      <button
                        onClick={() => viewOnMaps(favorite)}
                        className="text-sm font-medium rounded px-3 py-1 bg-blue-500 text-white hover:bg-blue-600"
                      >
                        View on Maps
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePage; 